import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  getUsers, inviteUser, deleteUser, startImpersonation,
  adminResendInvite, adminResetPassword, toggleDailySummary, adminClearLockout,
  updateNotifyProductTypes, updateUserRole, updateNotificationEmail, nudgeUser,
  adminSetPassword, adminLoginDiagnostic,
} from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Plus, Trash2, Users, Loader2, Copy, CheckCircle2, Eye, KeyRound, Mail, MoreVertical, Bell, Send } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

export default function UsersPage() {
  const { user: currentUser, checkAuth } = useAuth();
  const navigate = useNavigate();

  // Mirror of MessagesPage.getInitials fallback — used as placeholder hint in the prefs dialog
  const getInitialsAuto = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [credentialsResult, setCredentialsResult] = useState(null);
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [credentialsAction, setCredentialsAction] = useState(null);
  const [credentialsCopied, setCredentialsCopied] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [notifyDialogUser, setNotifyDialogUser] = useState(null);
  const [notifyPrefs, setNotifyPrefs] = useState([]);
  const [notifEmailDialogUser, setNotifEmailDialogUser] = useState(null);
  const [notifEmailValue, setNotifEmailValue] = useState("");
  const [notifInitialsValue, setNotifInitialsValue] = useState("");

  const PRODUCT_TYPE_LABELS = [
    { key: "marketing", label: "Marketing" },
    { key: "web_design", label: "Web Design" },
    { key: "seo_aeo", label: "SEO / AEO" },
    { key: "prospect_tools", label: "ProspectID / ProspectMax" },
  ];

  const openNotifyDialog = (u) => {
    const current = u.notify_product_types || ["marketing", "web_design", "seo_aeo", "prospect_tools"];
    setNotifyPrefs(current);
    setNotifyDialogUser(u);
  };

  const togglePref = (key) => {
    setNotifyPrefs((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
  };

  const saveNotifyPrefs = async () => {
    if (!notifyDialogUser) return;
    setActingId(notifyDialogUser.id);
    try {
      await updateNotifyProductTypes(notifyDialogUser.id, notifyPrefs);
      toast.success(`Notification preferences updated for ${notifyDialogUser.name}`);
      setNotifyDialogUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save preferences");
    } finally {
      setActingId(null);
    }
  };

  const openNotifEmailDialog = (u) => {
    setNotifEmailValue(u.notification_email || "");
    setNotifInitialsValue(u.display_initials || "");
    setNotifEmailDialogUser(u);
  };

  const saveNotifEmail = async () => {
    if (!notifEmailDialogUser) return;
    setActingId(notifEmailDialogUser.id);
    try {
      await updateNotificationEmail(notifEmailDialogUser.id, {
        notification_email: notifEmailValue.trim() || null,
        display_initials: notifInitialsValue.trim() || null,
      });
      toast.success(`Notification preferences saved for ${notifEmailDialogUser.name}`);
      setNotifEmailDialogUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setActingId(null);
    }
  };

  const handleChangeRole = async (u, newRole) => {
    const labels = {
      super_admin: "Super Admin",
      ops_coordinator: "Ops Coordinator",
      manager: "Manager",
      rep: "Sales Rep",
    };
    const label = labels[newRole] || newRole;
    const perks = {
      super_admin: "They will gain full super-admin privileges.",
      ops_coordinator: "They will lose super-admin privileges (no delete, no invite, no verify, no impersonate).",
      manager: "They will gain manager privileges (see the whole agency's clients).",
      rep: "They will lose manager privileges (only see clients assigned to them).",
    };
    if (!window.confirm(`Change ${u.name}'s role to ${label}? ${perks[newRole] || ""}`)) return;
    setActingId(u.id);
    try {
      await updateUserRole(u.id, newRole);
      toast.success(`${u.name} is now ${label}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change role");
    } finally {
      setActingId(null);
    }
  };

  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: currentUser?.role === "super_admin" ? "super_admin" : "rep",
  });

  const fetchUsers = async () => {
    try {
      const { data } = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setInviteResult(null);
    try {
      const { data } = await inviteUser(inviteForm);
      setInviteResult(data);
      setInviteForm({ email: "", name: "", role: currentUser?.role === "super_admin" ? "super_admin" : "rep" });
      fetchUsers();
    } catch (error) {
      console.error("Failed to invite user:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await deleteUser(selectedUser.id);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleImpersonate = async (targetUser) => {
    setImpersonatingId(targetUser.id);
    try {
      await startImpersonation(targetUser.id);
      await checkAuth();
      toast.success(`Now viewing as ${targetUser.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start impersonation");
    } finally {
      setImpersonatingId(null);
    }
  };

  const handleResendInvite = async (targetUser) => {
    setActingId(targetUser.id);
    try {
      const { data } = await adminResendInvite(targetUser.id);
      setCredentialsAction("resend");
      setCredentialsResult(data);
      toast.success(`Invite re-sent to ${data.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend invite");
    } finally {
      setActingId(null);
    }
  };

  const handleResetPassword = async (targetUser) => {
    if (!window.confirm(`Reset password for ${targetUser.name}? Their current password will stop working immediately.`)) return;
    setActingId(targetUser.id);
    try {
      const { data } = await adminResetPassword(targetUser.id);
      setCredentialsAction("reset");
      setCredentialsResult(data);
      toast.success("Password reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally {
      setActingId(null);
    }
  };

  const handleToggleDailySummary = async (targetUser) => {
    const next = !targetUser.daily_summary_disabled;
    const verb = next ? "pause" : "resume";
    if (!window.confirm(`${next ? "Pause" : "Resume"} daily summary emails for ${targetUser.name}?`)) return;
    setActingId(targetUser.id);
    try {
      await toggleDailySummary(targetUser.id, next);
      toast.success(`Daily summary ${verb}d for ${targetUser.name}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update setting");
    } finally {
      setActingId(null);
    }
  };

  const handleClearLockout = async (targetUser) => {
    setActingId(targetUser.id);
    try {
      const { data } = await adminClearLockout(targetUser.id);
      toast.success(data.cleared > 0
        ? `Lockout cleared for ${targetUser.name} (${data.cleared} record${data.cleared === 1 ? "" : "s"} removed). They can try logging in immediately.`
        : `${targetUser.name} had no active lockout — they can sign in now.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear lockout");
    } finally {
      setActingId(null);
    }
  };

  const handleNudge = async (targetUser) => {
    if (!window.confirm(`Send ${targetUser.name} a friendly reminder to log into the portal?`)) return;
    setActingId(targetUser.id);
    try {
      const { data } = await nudgeUser(targetUser.id);
      toast.success(`Nudge sent to ${data.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send nudge");
    } finally {
      setActingId(null);
    }
  };

  const handleDiagnoseLogin = async (targetUser) => {
    setActingId(targetUser.id);
    try {
      const { data } = await adminLoginDiagnostic(targetUser.id);
      setDiagnosticResult({ user: targetUser, ...data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not run diagnostic");
    } finally {
      setActingId(null);
    }
  };

  const handleSetSpecificPassword = async (targetUser) => {
    const pw = window.prompt(
      `Set a specific password for ${targetUser.name} (${targetUser.email}).\n\n` +
      `Use this when the auto-generated temp password isn't working (copy/paste issues, browser autofill, etc.).\n\n` +
      `Requirements: 8+ chars, must mix letters and numbers.\n\n` +
      `Example: BillMoore2026`
    );
    if (!pw) return;
    setActingId(targetUser.id);
    try {
      await adminSetPassword(targetUser.id, pw);
      toast.success(`Password set for ${targetUser.name}. They can log in immediately with the password you entered.`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set password");
    } finally {
      setActingId(null);
    }
  };

  const copyCredentials = () => {
    if (!credentialsResult) return;
    const text = `Email: ${credentialsResult.email}\nTemporary Password: ${credentialsResult.temp_password}`;
    navigator.clipboard.writeText(text);
    setCredentialsCopied(true);
    setTimeout(() => setCredentialsCopied(false), 2000);
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      super_admin: "role-super_admin",
      manager: "role-manager",
      rep: "role-rep",
      ops_coordinator: "role-manager",
    };
    return classes[role] || "role-rep";
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: "Super Admin",
      manager: "Manager",
      rep: "Sales Rep",
      ops_coordinator: "Ops Coordinator",
    };
    return labels[role] || role;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format last login as a human-readable relative time. Returns { label, tone }
  // so we can render "Never" / "23d ago" / "Yesterday" with a colored badge —
  // helpful for admins spotting reps who signed up but never logged in.
  const formatLastLogin = (iso, preExisting = false, preTrackingActive = false) => {
    if (!iso) {
      // 3-way pre-tracking state:
      //   Pre-existing + activity found → "Active" (green) — they've clearly used the portal
      //   Pre-existing + no activity     → "Unknown" (grey italic) — genuinely no signal
      //   Never invited / brand new      → "Never" (red) — Nudge candidate
      if (preExisting && preTrackingActive) {
        return { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };
      }
      if (preExisting) return { label: "Unknown", tone: "bg-zinc-100 text-zinc-600 border-zinc-200 italic" };
      return { label: "Never", tone: "bg-red-50 text-red-700 border-red-200" };
    }
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return { label: "Never", tone: "bg-red-50 text-red-700 border-red-200" };
    const diffMs = Date.now() - then;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    let label;
    let tone;
    if (diffMin < 5) label = "Just now";
    else if (diffMin < 60) label = `${diffMin}m ago`;
    else if (diffHr < 24) label = `${diffHr}h ago`;
    else if (diffDay === 1) label = "Yesterday";
    else if (diffDay < 30) label = `${diffDay}d ago`;
    else if (diffDay < 365) label = `${Math.floor(diffDay / 30)}mo ago`;
    else label = `${Math.floor(diffDay / 365)}y ago`;

    if (diffDay <= 7) tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
    else if (diffDay <= 30) tone = "bg-zinc-100 text-zinc-700 border-zinc-200";
    else tone = "bg-amber-50 text-amber-800 border-amber-200";
    return { label, tone };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground mt-1">
            {currentUser?.role === "super_admin" ? "All users across agencies" : "Users in your agency"}
          </p>
        </div>
        {(currentUser?.role === "manager" || currentUser?.role === "super_admin") && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setInviteResult(null);
          }}>
            <DialogTrigger asChild>
              <Button data-testid="invite-user-button">
                <Plus className="mr-2 h-4 w-4" />
                {currentUser?.role === "super_admin" ? "Invite Internal Team Member" : "Invite Rep"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {currentUser?.role === "super_admin" ? "Invite Internal Team Member" : "Invite Sales Rep"}
                </DialogTitle>
              </DialogHeader>
              {inviteResult ? (
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      User invited successfully! Share these credentials:
                    </AlertDescription>
                  </Alert>
                  <div className="space-y-3 p-4 bg-secondary rounded-lg">
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <p className="font-medium">{inviteResult.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Temporary Password</Label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-background rounded border text-sm">
                          {inviteResult.temp_password}
                        </code>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => copyToClipboard(inviteResult.temp_password)}
                        >
                          {copied ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => {
                    setDialogOpen(false);
                    setInviteResult(null);
                  }} className="w-full">
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleInviteUser} className="space-y-4">
                  {currentUser?.role === "super_admin" && (
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertDescription className="text-blue-900 text-xs">
                        This invites a Ready Prospect <b>internal team member</b>.
                        To add agency managers or reps, go to <b>Agencies</b> → select the agency → <b>Add User</b>.
                      </AlertDescription>
                    </Alert>
                  )}
                  {currentUser?.role === "super_admin" && (
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={inviteForm.role}
                        onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                      >
                        <SelectTrigger id="role" data-testid="invite-role-select">
                          <SelectValue placeholder="Pick a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="super_admin" data-testid="invite-role-super-admin">Super Admin (full access)</SelectItem>
                          <SelectItem value="ops_coordinator" data-testid="invite-role-ops-coordinator">Ops Coordinator (no delete, no admin actions)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Ops Coordinators see all orders / clients / proposals and can communicate, but cannot delete or invite users. They receive new-order notifications only after super-admin verification.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="user@example.com"
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                      placeholder="Full name"
                      required
                      data-testid="invite-name-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-invite-button">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Inviting...
                      </>
                    ) : (
                      "Send Invitation"
                    )}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Users List */}
      <Card className="border">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Users className="h-5 w-5" />
            All Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  {currentUser?.role === "super_admin" && <TableHead>Agency</TableHead>}
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="w-[180px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="table-row-hover">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {user.role !== "super_admin" ? (
                          <Link
                            to={`/users/${user.id}`}
                            className="text-primary hover:underline"
                            data-testid={`user-name-link-${user.id}`}
                            title={`View ${user.name}'s clients`}
                          >
                            {user.name}
                          </Link>
                        ) : (
                          <span>{user.name}</span>
                        )}
                        {user.daily_summary_disabled && (
                          <Badge variant="outline" className="text-xs border-amber-300 bg-amber-50 text-amber-800" title="Daily summary emails paused for this user">
                            Email Paused
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeClass(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    {currentUser?.role === "super_admin" && (
                      <TableCell className="text-muted-foreground">
                        {user.agency_name || "—"}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell data-testid={`last-login-${user.id}`}>
                      {(() => {
                        const { label, tone } = formatLastLogin(user.last_login_at, user.login_tracking_pre_existing, user.pre_tracking_active);
                        let tooltipText;
                        if (user.last_login_at) {
                          tooltipText = new Date(user.last_login_at).toLocaleString();
                        } else if (user.pre_tracking_active) {
                          tooltipText = "This user has clients/orders/messages in the portal, so they clearly used it — we just don't have an exact last-login timestamp yet. Their next login will populate the real date.";
                        } else if (user.login_tracking_pre_existing) {
                          tooltipText = "This user existed before login tracking was added and has no recorded activity — we can't tell if they've used the portal. Their next login will populate the real date.";
                        } else {
                          tooltipText = "This user has never logged into the portal";
                        }
                        return (
                          <Badge
                            variant="outline"
                            className={`text-xs font-medium ${tone}`}
                            title={tooltipText}
                          >
                            {label}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {currentUser?.role === "super_admin" ? (
                        <div className="flex items-center justify-end gap-1">
                          {/* Nudge inactive users — visible only for confirmed inactives:
                              (a) newly-invited users who NEVER logged in, or
                              (b) users whose last login was >30 days ago.
                              Skip users flagged as pre-existing (login_tracking_pre_existing)
                              because we can't tell if they're active or not until they log in
                              once under the new tracking. */}
                          {user.id !== currentUser?.id && (() => {
                            const iso = user.last_login_at;
                            const then = iso ? new Date(iso).getTime() : 0;
                            const daysAgo = iso ? Math.floor((Date.now() - then) / 86400000) : Infinity;
                            const isTrulyNever = !iso && !user.login_tracking_pre_existing;
                            const isStale = iso && daysAgo > 30;
                            // Pre-existing + no activity = ambiguous ("Unknown") — don't nudge them
                            //   until we know they're actually inactive.
                            // Pre-existing + activity = they're using the portal — no need to nudge.
                            const isInactive = isTrulyNever || isStale;
                            if (!isInactive) return null;
                            const nudgedIso = user.last_nudged_at;
                            const nudgedThen = nudgedIso ? new Date(nudgedIso).getTime() : 0;
                            const nudgedMinAgo = nudgedIso ? Math.floor((Date.now() - nudgedThen) / 60000) : Infinity;
                            const recentlyNudged = nudgedMinAgo < 60 * 24;  // suppress re-nudge < 24h
                            return (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2 text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={() => handleNudge(user)}
                                disabled={actingId === user.id || recentlyNudged}
                                data-testid={`nudge-${user.id}`}
                                title={recentlyNudged
                                  ? `Nudged ${nudgedMinAgo < 60 ? nudgedMinAgo + 'm' : Math.floor(nudgedMinAgo/60) + 'h'} ago — wait 24h before re-nudging`
                                  : `Send ${user.name} a friendly reminder to log into the portal`}
                              >
                                {actingId === user.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : recentlyNudged ? (
                                  <><CheckCircle2 className="h-4 w-4 mr-1" /> Nudged</>
                                ) : (
                                  <><Send className="h-4 w-4 mr-1" /> Nudge</>
                                )}
                              </Button>
                            );
                          })()}
                          {user.id !== currentUser?.id && user.role !== "super_admin" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-primary hover:text-primary hover:bg-primary/10"
                              onClick={() => handleImpersonate(user)}
                              disabled={impersonatingId === user.id || actingId === user.id}
                              data-testid={`view-as-${user.id}`}
                              title={`View the portal as ${user.name}`}
                            >
                              {impersonatingId === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <><Eye className="h-4 w-4 mr-1" /> View as</>
                              )}
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                disabled={actingId === user.id}
                                data-testid={`user-actions-${user.id}`}
                              >
                                {actingId === user.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreVertical className="h-4 w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              {user.id !== currentUser?.id && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => handleResendInvite(user)}
                                    data-testid={`resend-invite-${user.id}`}
                                  >
                                    <Mail className="h-4 w-4 mr-2" />
                                    Resend Invite (Email)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleResetPassword(user)}
                                    data-testid={`reset-password-${user.id}`}
                                  >
                                    <KeyRound className="h-4 w-4 mr-2" />
                                    Reset Password (No Email)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleSetSpecificPassword(user)}
                                    data-testid={`set-password-${user.id}`}
                                  >
                                    <KeyRound className="h-4 w-4 mr-2" />
                                    Set Specific Password...
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDiagnoseLogin(user)}
                                    data-testid={`diagnose-login-${user.id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Diagnose Login Issue
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleClearLockout(user)}
                                    data-testid={`clear-lockout-${user.id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Clear Login Lockout
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleToggleDailySummary(user)}
                                data-testid={`toggle-daily-summary-${user.id}`}
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                {user.daily_summary_disabled ? "Resume Daily Summary Emails" : "Pause Daily Summary Emails"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openNotifyDialog(user)}
                                data-testid={`notify-prefs-${user.id}`}
                              >
                                <Bell className="h-4 w-4 mr-2" />
                                Notification Settings
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openNotifEmailDialog(user)}
                                data-testid={`notif-email-${user.id}`}
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                Notification Email {user.id === currentUser?.id && "(yours)"}
                              </DropdownMenuItem>
                              {user.id !== currentUser?.id && !user.agency_id && (
                                <DropdownMenuItem
                                  onClick={() => handleChangeRole(user, user.role === "super_admin" ? "ops_coordinator" : "super_admin")}
                                  data-testid={`change-role-${user.id}`}
                                >
                                  <Users className="h-4 w-4 mr-2" />
                                  {user.role === "super_admin" ? "Demote to Ops Coordinator" : "Promote to Super Admin"}
                                </DropdownMenuItem>
                              )}
                              {user.id !== currentUser?.id && user.agency_id && (user.role === "rep" || user.role === "manager") && (
                                <DropdownMenuItem
                                  onClick={() => handleChangeRole(user, user.role === "manager" ? "rep" : "manager")}
                                  data-testid={`change-role-${user.id}`}
                                >
                                  <Users className="h-4 w-4 mr-2" />
                                  {user.role === "manager" ? "Demote to Sales Rep" : "Promote to Manager"}
                                </DropdownMenuItem>
                              )}
                              {user.id !== currentUser?.id && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedUser(user);
                                      setDeleteDialogOpen(true);
                                    }}
                                    className="text-destructive focus:text-destructive"
                                    data-testid={`delete-user-${user.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete User
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{selectedUser?.name}</strong>. Their clients and projects will remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-user"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diagnostic Result Dialog (shown after Diagnose Login Issue) */}
      <Dialog open={!!diagnosticResult} onOpenChange={(o) => { if (!o) setDiagnosticResult(null); }}>
        <DialogContent data-testid="diagnostic-result-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Login Diagnostic — {diagnosticResult?.user?.name}</DialogTitle>
          </DialogHeader>
          {diagnosticResult && (
            <div className="space-y-3 text-sm">
              <div className={`rounded-lg border p-3 ${diagnosticResult.active_lockout ? "bg-red-50 border-red-300" : "bg-emerald-50 border-emerald-300"}`}>
                <div className="font-semibold mb-1">{diagnosticResult.active_lockout ? "🔒 Account Locked" : "✅ No obvious backend blocker"}</div>
                <div className="text-xs text-muted-foreground">{diagnosticResult.diagnostic_summary}</div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 border rounded p-3 bg-muted/20">
                <div className="text-xs text-muted-foreground">Email</div>
                <div className="text-xs font-medium truncate" title={diagnosticResult.email}>{diagnosticResult.email}</div>

                <div className="text-xs text-muted-foreground">Role</div>
                <div className="text-xs font-medium">{diagnosticResult.role}</div>

                <div className="text-xs text-muted-foreground">Has password on file</div>
                <div className="text-xs font-medium">{diagnosticResult.has_password_hash ? "Yes" : "❌ No"}</div>

                <div className="text-xs text-muted-foreground">Last successful login</div>
                <div className="text-xs font-medium">
                  {diagnosticResult.last_login_at ? new Date(diagnosticResult.last_login_at).toLocaleString() : <span className="text-red-600">Never</span>}
                </div>

                <div className="text-xs text-muted-foreground">Last password reset</div>
                <div className="text-xs font-medium">
                  {diagnosticResult.last_password_reset_at
                    ? `${new Date(diagnosticResult.last_password_reset_at).toLocaleString()} by ${diagnosticResult.last_password_reset_by || "admin"}`
                    : "Never"}
                </div>

                <div className="text-xs text-muted-foreground">Outstanding reset tokens</div>
                <div className="text-xs font-medium">{diagnosticResult.outstanding_reset_tokens}</div>

                <div className="text-xs text-muted-foreground">Password set manually</div>
                <div className="text-xs font-medium">{diagnosticResult.password_set_manually ? "Yes" : "No (auto-generated)"}</div>
              </div>

              {diagnosticResult.active_lockout && (
                <Alert variant="destructive" className="text-xs">
                  <AlertDescription>
                    Lockout expires {new Date(diagnosticResult.active_lockout.expires_at).toLocaleTimeString()}. Click <b>Clear Login Lockout</b> in the actions menu to unblock immediately.
                  </AlertDescription>
                </Alert>
              )}

              {!diagnosticResult.active_lockout && !diagnosticResult.last_login_at && (
                <Alert className="text-xs bg-amber-50 border-amber-300">
                  <AlertDescription>
                    User has a valid password on file but has NEVER logged in successfully. Most likely: they're typing the wrong password, using an older temp password (each reset invalidates the previous), or hitting a copy/paste issue. Try <b>Set Specific Password</b> from the actions menu — pick a memorable password and dictate it to them over the phone.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end">
                <Button onClick={() => setDiagnosticResult(null)} data-testid="diagnostic-close-btn">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Credentials Result Dialog (shown after Resend Invite or Reset Password) */}
      <Dialog
        open={!!credentialsResult}
        onOpenChange={(o) => {
          if (!o) {
            setCredentialsResult(null);
            setCredentialsAction(null);
            setCredentialsCopied(false);
          }
        }}
      >
        <DialogContent data-testid="credentials-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {credentialsAction === "resend"
                ? "Invite Re-Sent"
                : "Password Reset"}
            </DialogTitle>
          </DialogHeader>
          {credentialsResult && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {credentialsAction === "resend" ? (
                  <>
                    A fresh invite email has been sent to <b>{credentialsResult.email}</b>. The previous password is now invalid.
                  </>
                ) : (
                  <>
                    A new temporary password has been generated for <b>{credentialsResult.name}</b>. <b>No email was sent</b> — share these credentials over Slack, SMS, or in person.
                  </>
                )}
              </p>
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Email</div>
                <div className="font-medium">{credentialsResult.email}</div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground pt-2">Temporary password</div>
                <div className="font-mono text-base bg-background border rounded px-3 py-2 select-all">
                  {credentialsResult.temp_password}
                </div>
              </div>
              <Alert>
                <AlertDescription className="text-xs">
                  Tell the user to type the password manually (don't copy-paste in some email clients) or use the "Forgot password?" link if they have trouble.
                </AlertDescription>
              </Alert>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={copyCredentials}
                  data-testid="copy-credentials-btn"
                >
                  {credentialsCopied ? (
                    <><CheckCircle2 className="h-4 w-4 mr-2 text-green-600" /> Copied!</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-2" /> Copy Email + Password</>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setCredentialsResult(null);
                    setCredentialsAction(null);
                  }}
                  data-testid="credentials-result-close-btn"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-user notification preferences dialog */}
      <Dialog open={!!notifyDialogUser} onOpenChange={(open) => !open && setNotifyDialogUser(null)}>
        <DialogContent className="max-w-md" data-testid="notify-prefs-dialog">
          <DialogHeader>
            <DialogTitle>Notification Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {notifyDialogUser && (
              <>
                <p className="text-sm text-muted-foreground">
                  Choose which product types <b>{notifyDialogUser.name}</b> should be notified about when a new order is verified.
                </p>
                <div className="space-y-2 border rounded-md p-3 bg-secondary/30">
                  {PRODUCT_TYPE_LABELS.map((p) => {
                    const checked = notifyPrefs.includes(p.key);
                    return (
                      <label
                        key={p.key}
                        className="flex items-center gap-3 cursor-pointer py-1.5"
                        data-testid={`notify-pref-${p.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePref(p.key)}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                        <span className="text-sm">{p.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setNotifyDialogUser(null)} data-testid="notify-prefs-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={saveNotifyPrefs}
                    disabled={actingId === notifyDialogUser.id}
                    data-testid="notify-prefs-save"
                  >
                    {actingId === notifyDialogUser.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Notification Email override dialog */}
      <Dialog open={!!notifEmailDialogUser} onOpenChange={(open) => !open && setNotifEmailDialogUser(null)}>
        <DialogContent className="max-w-md" data-testid="notif-email-dialog">
          <DialogHeader>
            <DialogTitle>Notification Preferences</DialogTitle>
          </DialogHeader>
          {notifEmailDialogUser && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Edit how <b>{notifEmailDialogUser.name}</b> receives notifications and how their replies are tagged in the unified inbox.
              </p>
              <div>
                <Label htmlFor="notif-email-input">Send notifications to</Label>
                <Input
                  id="notif-email-input"
                  type="email"
                  placeholder={notifEmailDialogUser.email}
                  value={notifEmailValue}
                  onChange={(e) => setNotifEmailValue(e.target.value)}
                  data-testid="notif-email-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Leave blank to use the login email (<b>{notifEmailDialogUser.email}</b>).
                </p>
              </div>
              <div>
                <Label htmlFor="notif-initials-input">Display initials</Label>
                <Input
                  id="notif-initials-input"
                  type="text"
                  maxLength={4}
                  placeholder="e.g. Dar, Dan, LA"
                  value={notifInitialsValue}
                  onChange={(e) => setNotifInitialsValue(e.target.value)}
                  data-testid="notif-initials-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Shown in the inbox "{notifInitialsValue || getInitialsAuto(notifEmailDialogUser.name)} Replied" pills. Up to 4 characters.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setNotifEmailDialogUser(null)} data-testid="notif-email-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={saveNotifEmail}
                  disabled={actingId === notifEmailDialogUser.id}
                  data-testid="notif-email-save"
                >
                  {actingId === notifEmailDialogUser.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
